import { useEffect, useMemo, useRef, useState } from "react";
import * as Dialog from "@radix-ui/react-dialog";
import {
    createColumnHelper,
    flexRender,
    getCoreRowModel,
    useReactTable,
} from "@tanstack/react-table";
// react-virtual import removed (2026-04-26): the virtualisation attempt
// broke <table> column alignment. See note in the table render below.
import {
    Download,
    Search,
    ShieldAlert,
    ShieldX,
    SlidersHorizontal,
    Tag as TagIcon,
    Trash2,
    X,
} from "lucide-react";
import { toast } from "sonner";

import { ApiError } from "~/lib/api";
import { useCurrentUser } from "~/lib/auth";
import {
    BULK_THRESHOLD,
    KILLCHAIN_PHASES,
    type BulkIoCInput,
    type IoCRecord,
    type KillchainPhase,
    useBulkIoC,
    useFeedSources,
    useIoCStats,
    useIoCs,
} from "~/lib/iocs";
import {
    TypeBadge,
    severityLabel,
    severityPillClass,
} from "~/lib/iocVisuals";
import { IOC_TYPES } from "~/lib/licenses";

import { IoCDetailDialog } from "~/components/IoCDetailDialog";
import { Pagination } from "~/components/Pagination";
import { SavedViewsMenu } from "~/components/SavedViewsMenu";
import { SelectionToolbar } from "~/components/SelectionToolbar";
import { TableSkeleton } from "~/components/TableSkeleton";

// PAGE_SIZE is the server-side page; the table is virtualized so we can
// safely fetch a larger window without paying the DOM cost. The list page
// uses TanStack Table v8 for column definitions; virtualisation was reverted
// (see VirtualIoCTable comment for context) so the viewport just renders
// PAGE_SIZE rows directly. If we ever raise PAGE_SIZE above ~2000 we'll
// re-add row virtualisation via a CSS-grid shape (not a <table> element).
const PAGE_SIZE = 200;

export function IoCsPage(): React.ReactElement {
    const me = useCurrentUser();
    const canEdit = me.data?.roles.includes("admin") || me.data?.roles.includes("operator");
    const canDelete = me.data?.roles.includes("admin");

    const [types, setTypes] = useState<string[]>([]);
    const [search, setSearch] = useState("");
    const [debouncedSearch, setDebouncedSearch] = useState("");
    const [minSeverity, setMinSeverity] = useState(1);
    const [source, setSource] = useState("");
    const [killchain, setKillchain] = useState<KillchainPhase | "unclassified" | "">("");
    const [page, setPage] = useState(0);
    const [active, setActive] = useState<string | null>(null);

    // Selection state (multi-select across rows on the current page).
    const [selectedIds, setSelectedIds] = useState<Set<string>>(() => new Set());
    // When true, actions target the whole filtered set, not just selectedIds.
    const [allMatching, setAllMatching] = useState(false);
    // Last-toggled index for shift-click range selection.
    const lastToggledIndexRef = useRef<number | null>(null);

    useDebounce(search, 250, (v) => {
        setDebouncedSearch(v);
        setPage(0);
    });

    const offset = page * PAGE_SIZE;
    const stats = useIoCStats();
    const feeds = useFeedSources();
    const result = useIoCs({
        types: types.length ? types : undefined,
        search: debouncedSearch || undefined,
        minSeverity: minSeverity > 1 ? minSeverity : undefined,
        source: source || undefined,
        killchain: killchain || undefined,
        limit: PAGE_SIZE,
        offset,
    });
    const iocs = result.data?.iocs ?? [];
    const total = result.data?.total ?? 0;
    const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
    const filtersActive = types.length > 0 || debouncedSearch || minSeverity > 1 || source || killchain;

    function clearFilters(): void {
        setTypes([]);
        setSearch("");
        setMinSeverity(1);
        setSource("");
        setKillchain("");
        setPage(0);
    }

    /** applySavedViewFilter is the inverse of the URL-param shape — saved
     * views store the same keys we send to /iocs, so we just destructure
     * with safe coercions and ignore unknown keys (forward-compat). */
    function applySavedViewFilter(filter: Record<string, unknown>): void {
        const t = filter.types;
        setTypes(Array.isArray(t) ? (t.filter((x) => typeof x === "string") as string[]) : []);
        setSearch(typeof filter.search === "string" ? filter.search : "");
        setMinSeverity(typeof filter.minSeverity === "number" ? filter.minSeverity : 1);
        setSource(typeof filter.source === "string" ? filter.source : "");
        setKillchain(
            typeof filter.killchain === "string"
                ? (filter.killchain as KillchainPhase | "unclassified")
                : "",
        );
        setPage(0);
    }

    /** currentSavedFilter is the snapshot the SavedViewsMenu sends back
     * if the operator clicks "Save current filter as view". */
    const currentSavedFilter: Record<string, unknown> = {
        types: types.length ? types : undefined,
        search: debouncedSearch || undefined,
        minSeverity: minSeverity > 1 ? minSeverity : undefined,
        source: source || undefined,
        killchain: killchain || undefined,
    };

    // Filters changing invalidates any selection: the ids on the new page
    // may not include what was selected, and "all matching" is now a
    // different set. We clear to avoid surprise.
    const filterFingerprint = JSON.stringify({ types, debouncedSearch, minSeverity, source, killchain });
    useEffect(() => {
        setSelectedIds(new Set());
        setAllMatching(false);
    }, [filterFingerprint]);

    // Bulk action wiring.
    const bulk = useBulkIoC();
    const currentFilters: BulkIoCInput["filters"] = {
        types: types.length ? types : undefined,
        search: debouncedSearch || undefined,
        source: source || undefined,
        minSeverity: minSeverity > 1 ? minSeverity : undefined,
        killchain: killchain || undefined,
    };

    /** Toggle a single row's selection. Supports shift-click range extension. */
    function toggleRowSelect(id: string, indexInPage: number, shiftKey: boolean): void {
        setAllMatching(false);
        setSelectedIds((prev) => {
            const next = new Set(prev);
            const last = lastToggledIndexRef.current;
            if (shiftKey && last !== null && last !== indexInPage) {
                const [lo, hi] = last < indexInPage ? [last, indexInPage] : [indexInPage, last];
                const want = !prev.has(id); // match the end state of the clicked row
                for (let i = lo; i <= hi; i++) {
                    const rowId = iocs[i]?.id;
                    if (!rowId) continue;
                    if (want) next.add(rowId);
                    else next.delete(rowId);
                }
            } else if (next.has(id)) {
                next.delete(id);
            } else {
                next.add(id);
            }
            return next;
        });
        lastToggledIndexRef.current = indexInPage;
    }

    function togglePageSelect(allSelected: boolean): void {
        setAllMatching(false);
        if (allSelected) {
            setSelectedIds(new Set());
        } else {
            setSelectedIds(new Set(iocs.map((i) => i.id)));
        }
        lastToggledIndexRef.current = null;
    }

    function clearSelection(): void {
        setSelectedIds(new Set());
        setAllMatching(false);
        lastToggledIndexRef.current = null;
    }

    async function runBulk(action: BulkIoCInput["action"], extras: Partial<BulkIoCInput> = {}): Promise<void> {
        const payload: BulkIoCInput = {
            action,
            ...(allMatching
                ? { filters: currentFilters }
                : { iocIds: Array.from(selectedIds) }),
            ...extras,
        };
        // Client-side threshold warning before we hit the server's
        // 412 round-trip — UX is snappier.
        const size = allMatching ? total : selectedIds.size;
        if (size > BULK_THRESHOLD && !extras.confirmLarge) {
            const ok = window.confirm(
                `This affects ${size.toLocaleString()} IoCs — above the ${BULK_THRESHOLD.toLocaleString()} threshold.\n\n` +
                    `Large-scale operations can't be undone. Proceed?`,
            );
            if (!ok) return;
            payload.confirmLarge = true;
        }

        try {
            const res = await bulk.mutateAsync(payload);
            toast.success(`${verbForAction(action)} ${res.affected.toLocaleString()} IoC${res.affected === 1 ? "" : "s"}`);
            clearSelection();
        } catch (err) {
            toast.error(err instanceof ApiError ? err.message : "Bulk action failed");
        }
    }

    /** Export the currently-loaded page to CSV (client-side; same fields as the table). */
    function exportCSV(): void {
        const rows: IoCRecord[] = allMatching ? iocs : iocs.filter((i) => selectedIds.has(i.id));
        if (rows.length === 0) {
            toast.error("Nothing to export");
            return;
        }
        const header = ["id", "type", "value", "severity", "sourceCount", "killchainPhase", "firstSeen", "lastSeen", "tags"];
        const escape = (s: string): string => `"${s.replace(/"/g, '""')}"`;
        const csv = [header.join(",")]
            .concat(
                rows.map((r) =>
                    [
                        r.id,
                        r.type,
                        r.value,
                        String(r.severity),
                        String(r.sourceCount),
                        r.killchainPhase ?? "",
                        r.firstSeen,
                        r.lastSeen,
                        r.tags.join(";"),
                    ]
                        .map(escape)
                        .join(","),
                ),
            )
            .join("\n");
        const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `iocs-${new Date().toISOString().slice(0, 10)}.csv`;
        a.click();
        URL.revokeObjectURL(url);
        toast.success(`Exported ${rows.length.toLocaleString()} rows`);
    }

    function toggleType(slug: string): void {
        setTypes((cur) => (cur.includes(slug) ? cur.filter((t) => t !== slug) : [...cur, slug]));
        setPage(0);
    }

    const filterCount =
        (types.length > 0 ? 1 : 0) +
        (debouncedSearch ? 1 : 0) +
        (minSeverity > 1 ? 1 : 0) +
        (source ? 1 : 0) +
        (killchain ? 1 : 0);

    return (
        <div className="stack-lg">
            <header className="page-header">
                <div className="page-header__top">
                    <div className="page-header__heading">
                        <span className="page-header__eyebrow">
                            <span className="page-header__eyebrow__icon">
                                <ShieldAlert size={14} aria-hidden />
                            </span>
                            Intelligence
                        </span>
                        <h1 className="page-header__title">Indicators of Compromise</h1>
                        <p className="page-header__description">
                            Every indicator the platform has ever ingested, deduplicated and scored
                            across feeds. Filter by type, severity, source feed, or kill-chain phase
                            and bulk-act on the matching set.
                        </p>
                    </div>
                </div>
                <div className="page-header__meta">
                    <span className="page-header__meta__item tabular-nums">
                        {(stats.data?.total ?? 0).toLocaleString()} tracked
                    </span>
                    {filterCount > 0 && (
                        <span className="page-header__meta__item">
                            {filterCount} filter{filterCount === 1 ? "" : "s"} applied
                        </span>
                    )}
                    {!result.isFetching && result.dataUpdatedAt > 0 && (
                        <span className="page-header__meta__item">
                            Updated {new Date(result.dataUpdatedAt).toLocaleTimeString()}
                        </span>
                    )}
                </div>
            </header>

            {/* KPI metric grid */}
            <section className="metric-grid" aria-label="IoC metrics">
                <div className="metric-card">
                    <span className="metric-card__label">Tracked IoCs</span>
                    <span className="metric-card__value">
                        {stats.data ? stats.data.total.toLocaleString() : "—"}
                    </span>
                    <span className="metric-card__sub">
                        {stats.data?.byType
                            ? `${stats.data.byType.length} type${stats.data.byType.length === 1 ? "" : "s"}`
                            : "Across all feeds"}
                    </span>
                </div>
                <div className="metric-card metric-card--accent">
                    <span className="metric-card__label">Sightings · 1h</span>
                    <span className="metric-card__value">
                        {stats.data ? stats.data.sightings1h.toLocaleString() : "—"}
                    </span>
                    <span className="metric-card__sub">Live ingest velocity</span>
                </div>
                <div className="metric-card">
                    <span className="metric-card__label">Sightings · 24h</span>
                    <span className="metric-card__value">
                        {stats.data ? stats.data.sightings24h.toLocaleString() : "—"}
                    </span>
                    <span className="metric-card__sub">Rolling window</span>
                </div>
                <div className="metric-card">
                    <span className="metric-card__label">Top source</span>
                    <span className="metric-card__value metric-card__value--sm">
                        {stats.data?.bySource?.[0]?.key ?? "—"}
                    </span>
                    <span className="metric-card__sub">
                        {stats.data?.bySource?.[0]
                            ? `${stats.data.bySource[0].count.toLocaleString()} sightings`
                            : "Across all feeds"}
                    </span>
                </div>
            </section>

            {/* Filter section */}
            <section className="section-card" aria-label="Filters">
                <div className="toolbar">
                    <div className="toolbar__search">
                        <Search size={14} className="toolbar__search__icon" aria-hidden />
                        <input
                            type="search"
                            className="input"
                            placeholder="Search by indicator value (substring)…"
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                        />
                    </div>

                    <select
                        className="input"
                        value={source}
                        onChange={(e) => {
                            setSource(e.target.value);
                            setPage(0);
                        }}
                        style={{ width: 200, flexShrink: 0 }}
                        aria-label="Source feed"
                    >
                        <option value="">All sources</option>
                        {feeds.data?.map((f) => (
                            <option key={f.slug} value={f.slug}>
                                {f.slug}
                            </option>
                        ))}
                    </select>

                    <div className="toolbar__filter">
                        <SlidersHorizontal size={14} className="text-text-subtle" aria-hidden />
                        <span className="toolbar__filter__label">Min sev</span>
                        <select
                            className="input"
                            value={minSeverity}
                            onChange={(e) => {
                                setMinSeverity(Number(e.target.value));
                                setPage(0);
                            }}
                            style={{ width: 96 }}
                            aria-label="Minimum severity"
                        >
                            {[1, 2, 4, 7, 9].map((n) => (
                                <option key={n} value={n}>
                                    ≥ {n} {n > 1 ? `(${severityLabel(n)})` : ""}
                                </option>
                            ))}
                        </select>
                    </div>

                    <select
                        className="input"
                        value={killchain}
                        onChange={(e) => {
                            setKillchain(e.target.value as KillchainPhase | "unclassified" | "");
                            setPage(0);
                        }}
                        style={{ width: 200, flexShrink: 0 }}
                        title="Kill-chain phase filter"
                        aria-label="Kill-chain phase"
                    >
                        <option value="">All kill-chain phases</option>
                        <option value="unclassified">Unclassified (no phase)</option>
                        {KILLCHAIN_PHASES.map((p) => (
                            <option key={p.slug} value={p.slug}>
                                {p.label}
                            </option>
                        ))}
                    </select>

                    <div className="toolbar__actions cluster-xs">
                        <SavedViewsMenu
                            currentFilter={currentSavedFilter}
                            hasFilters={!!filtersActive}
                            onApply={applySavedViewFilter}
                        />
                        {filtersActive && (
                            <button type="button" className="btn btn--ghost btn--sm press-crush" onClick={clearFilters}>
                                <X size={12} aria-hidden />
                                <span>Clear</span>
                            </button>
                        )}
                    </div>
                </div>

                <div className="toolbar__chip-row">
                    {IOC_TYPES.map((t) => {
                        const active = types.includes(t.slug);
                        return (
                            <button
                                key={t.slug}
                                type="button"
                                onClick={() => toggleType(t.slug)}
                                className={active ? "chip chip--accent" : "chip"}
                                style={{
                                    cursor: "pointer",
                                    height: 24,
                                    opacity: active ? 1 : 0.75,
                                    transition: "opacity var(--duration-fast) var(--ease-standard)",
                                }}
                                aria-pressed={active}
                            >
                                {t.label}
                            </button>
                        );
                    })}
                </div>
            </section>

            {/* Results */}
            <section className="section-card" aria-label="IoC results">
                {result.isLoading && <TableSkeleton rows={10} columns={6} />}
                {result.isError && (
                    <div className="empty-state">
                        <ShieldX size={20} className="empty-state__icon" aria-hidden />
                        <div className="empty-state__title">Could not load IoCs</div>
                        <div className="empty-state__body">
                            {result.error instanceof Error ? result.error.message : "Unknown error"}
                        </div>
                    </div>
                )}
                {result.data && iocs.length === 0 && (
                    <div className="empty-state">
                        <ShieldAlert size={20} className="empty-state__icon" aria-hidden />
                        <div className="empty-state__title">No IoCs match your filters</div>
                        <div className="empty-state__body">
                            Loosen the filters above, or wait for the next ingest tick.
                        </div>
                    </div>
                )}
                {result.data && iocs.length > 0 && (
                    <>
                        <VirtualIoCTable
                            iocs={iocs}
                            total={total}
                            canEdit={!!canEdit}
                            allMatching={allMatching}
                            selectedIds={selectedIds}
                            setAllMatching={setAllMatching}
                            onToggleSelectRow={toggleRowSelect}
                            onTogglePageSelect={togglePageSelect}
                            onOpen={(id) => setActive(id)}
                        />

                        <Pagination
                            page={page}
                            totalPages={totalPages}
                            total={total}
                            offset={offset}
                            shown={iocs.length}
                            onChange={setPage}
                        />
                    </>
                )}
            </section>

            <IoCDetailDialog iocId={active} onClose={() => setActive(null)} />

            {canEdit && (
                <SelectionToolbar
                    count={allMatching ? total : selectedIds.size}
                    total={total}
                    allMatching={allMatching}
                    onClear={clearSelection}
                >
                    <BulkTagButton onRun={(tags) => runBulk("add_tags", { tags })} />
                    <BulkSeverityButton onRun={(severity) => runBulk("set_severity", { severity })} />
                    <BulkKillchainButton onRun={(killchainPhase) => runBulk("set_killchain", { killchainPhase })} />
                    <button
                        type="button"
                        className="btn btn--secondary btn--sm press-crush"
                        onClick={exportCSV}
                        title="Export current selection (or page) to CSV"
                    >
                        <Download size={14} /> <span>Export CSV</span>
                    </button>
                    {canDelete && (
                        <BulkDeleteButton
                            count={allMatching ? total : selectedIds.size}
                            onRun={() => runBulk("delete")}
                        />
                    )}
                </SelectionToolbar>
            )}
        </div>
    );
}

function verbForAction(action: BulkIoCInput["action"]): string {
    switch (action) {
        case "add_tags":        return "Tagged";
        case "remove_tags":     return "Untagged";
        case "set_severity":    return "Set severity on";
        case "set_killchain":   return "Set kill-chain on";
        case "delete":          return "Deleted";
    }
}

interface HeaderCheckboxProps {
    total: number;
    selectedOnPage: number;
    onToggle: (allSelectedOnPage: boolean) => void;
}

/**
 * Tri-state checkbox: checked when every row on the page is selected,
 * indeterminate when a subset is selected, empty when none are. Handled
 * via the native `indeterminate` property on the DOM node.
 */
function HeaderCheckbox({ total, selectedOnPage, onToggle }: HeaderCheckboxProps): React.ReactElement {
    const ref = useRef<HTMLInputElement>(null);
    const allSelected = total > 0 && selectedOnPage === total;
    const partial = selectedOnPage > 0 && selectedOnPage < total;
    useEffect(() => {
        if (ref.current) ref.current.indeterminate = partial;
    }, [partial]);
    return (
        <input
            ref={ref}
            type="checkbox"
            aria-label={allSelected ? "Deselect all on page" : "Select all on page"}
            checked={allSelected}
            onChange={() => onToggle(allSelected)}
            style={{ cursor: "pointer" }}
        />
    );
}

function BulkTagButton({ onRun }: { onRun: (tags: string[]) => void }): React.ReactElement {
    const [open, setOpen] = useState(false);
    const [input, setInput] = useState("");
    return (
        <>
            <button
                type="button"
                className="btn btn--secondary btn--sm press-crush"
                onClick={() => setOpen(true)}
            >
                <TagIcon size={14} /> <span>Add tag</span>
            </button>
            <Dialog.Root open={open} onOpenChange={setOpen}>
                <Dialog.Portal>
                    <Dialog.Overlay className="dialog-overlay">
                        <Dialog.Content className="dialog-content enter-up" style={{ padding: "var(--space-6)", maxWidth: 440 }}>
                            <Dialog.Title className="type-h2">Add tag to selected IoCs</Dialog.Title>
                            <Dialog.Description className="text-12 text-text-muted" style={{ marginTop: "var(--space-2)" }}>
                                Comma-separate to add multiple tags at once.
                            </Dialog.Description>
                            <form
                                onSubmit={(e) => {
                                    e.preventDefault();
                                    const tags = input.split(",").map((t) => t.trim()).filter(Boolean);
                                    if (tags.length === 0) return;
                                    onRun(tags);
                                    setOpen(false);
                                    setInput("");
                                }}
                                className="stack"
                                style={{ marginTop: "var(--space-4)" }}
                            >
                                <input
                                    autoFocus
                                    className="input"
                                    value={input}
                                    onChange={(e) => setInput(e.target.value)}
                                    placeholder="curated, high-confidence"
                                />
                                <button type="submit" className="btn btn--primary press-crush">
                                    <TagIcon size={14} /> <span>Add tags</span>
                                </button>
                            </form>
                        </Dialog.Content>
                    </Dialog.Overlay>
                </Dialog.Portal>
            </Dialog.Root>
        </>
    );
}

function BulkSeverityButton({ onRun }: { onRun: (severity: number) => void }): React.ReactElement {
    const [open, setOpen] = useState(false);
    const [value, setValue] = useState(5);
    return (
        <>
            <button
                type="button"
                className="btn btn--secondary btn--sm press-crush"
                onClick={() => setOpen(true)}
            >
                <SlidersHorizontal size={14} /> <span>Set severity</span>
            </button>
            <Dialog.Root open={open} onOpenChange={setOpen}>
                <Dialog.Portal>
                    <Dialog.Overlay className="dialog-overlay">
                        <Dialog.Content className="dialog-content enter-up" style={{ padding: "var(--space-6)", maxWidth: 440 }}>
                            <Dialog.Title className="type-h2">Set severity on selected IoCs</Dialog.Title>
                            <Dialog.Description className="text-12 text-text-muted" style={{ marginTop: "var(--space-2)" }}>
                                Severity is marked as operator-overridden so subsequent re-ingests
                                won't clobber your choice.
                            </Dialog.Description>
                            <div className="cluster-xs" style={{ marginTop: "var(--space-4)", gap: 4, flexWrap: "wrap" }}>
                                {Array.from({ length: 10 }, (_, i) => i + 1).map((n) => (
                                    <button
                                        key={n}
                                        type="button"
                                        onClick={() => setValue(n)}
                                        className={n <= value ? severityPillClass(n) : ""}
                                        style={{
                                            width: 36,
                                            height: 32,
                                            border: n <= value ? 0 : "var(--border-hairline) solid var(--color-border)",
                                            borderRadius: "var(--radius-sm)",
                                            cursor: "pointer",
                                            color: n <= value ? undefined : "var(--color-text-muted)",
                                            fontFamily: "var(--font-family-mono)",
                                        }}
                                    >
                                        {n}
                                    </button>
                                ))}
                            </div>
                            <span className="text-12 text-text-muted" style={{ display: "block", marginTop: "var(--space-2)" }}>
                                {value} · {severityLabel(value)}
                            </span>
                            <button
                                type="button"
                                className="btn btn--primary press-crush"
                                style={{ marginTop: "var(--space-4)" }}
                                onClick={() => {
                                    onRun(value);
                                    setOpen(false);
                                }}
                            >
                                Apply
                            </button>
                        </Dialog.Content>
                    </Dialog.Overlay>
                </Dialog.Portal>
            </Dialog.Root>
        </>
    );
}

function BulkKillchainButton({ onRun }: { onRun: (phase: string) => void }): React.ReactElement {
    const [open, setOpen] = useState(false);
    const [phase, setPhase] = useState("");
    return (
        <>
            <button
                type="button"
                className="btn btn--secondary btn--sm press-crush"
                onClick={() => setOpen(true)}
            >
                <SlidersHorizontal size={14} /> <span>Set kill-chain</span>
            </button>
            <Dialog.Root open={open} onOpenChange={setOpen}>
                <Dialog.Portal>
                    <Dialog.Overlay className="dialog-overlay">
                        <Dialog.Content className="dialog-content enter-up" style={{ padding: "var(--space-6)", maxWidth: 440 }}>
                            <Dialog.Title className="type-h2">Set kill-chain phase</Dialog.Title>
                            <select
                                className="input"
                                value={phase}
                                onChange={(e) => setPhase(e.target.value)}
                                style={{ marginTop: "var(--space-4)" }}
                            >
                                <option value="">— clear (unclassified) —</option>
                                {KILLCHAIN_PHASES.map((p) => (
                                    <option key={p.slug} value={p.slug}>{p.label}</option>
                                ))}
                            </select>
                            <button
                                type="button"
                                className="btn btn--primary press-crush"
                                style={{ marginTop: "var(--space-4)" }}
                                onClick={() => {
                                    onRun(phase);
                                    setOpen(false);
                                    setPhase("");
                                }}
                            >
                                Apply
                            </button>
                        </Dialog.Content>
                    </Dialog.Overlay>
                </Dialog.Portal>
            </Dialog.Root>
        </>
    );
}

function BulkDeleteButton({ count, onRun }: { count: number; onRun: () => void }): React.ReactElement {
    const [open, setOpen] = useState(false);
    return (
        <>
            <button
                type="button"
                className="btn btn--danger btn--sm press-crush"
                onClick={() => setOpen(true)}
            >
                <Trash2 size={14} /> <span>Delete</span>
            </button>
            <Dialog.Root open={open} onOpenChange={setOpen}>
                <Dialog.Portal>
                    <Dialog.Overlay className="dialog-overlay">
                        <Dialog.Content className="dialog-content enter-up" style={{ padding: "var(--space-6)", maxWidth: 440 }}>
                            <Dialog.Title className="type-h2">Delete {count.toLocaleString()} IoCs?</Dialog.Title>
                            <Dialog.Description className="text-text-muted text-13" style={{ marginTop: "var(--space-2)" }}>
                                This is irreversible. Ingested source sightings are dropped along with the IoC.
                                {count > BULK_THRESHOLD && (
                                    <>
                                        <br />
                                        <strong>This exceeds the {BULK_THRESHOLD.toLocaleString()}-row threshold;</strong>{" "}
                                        admin confirmation required.
                                    </>
                                )}
                            </Dialog.Description>
                            <div className="cluster-xs" style={{ marginTop: "var(--space-4)", justifyContent: "flex-end" }}>
                                <Dialog.Close asChild>
                                    <button type="button" className="btn btn--ghost">Cancel</button>
                                </Dialog.Close>
                                <button
                                    type="button"
                                    className="btn btn--danger press-crush"
                                    onClick={() => {
                                        onRun();
                                        setOpen(false);
                                    }}
                                >
                                    <Trash2 size={14} /> <span>Delete {count.toLocaleString()}</span>
                                </button>
                            </div>
                        </Dialog.Content>
                    </Dialog.Overlay>
                </Dialog.Portal>
            </Dialog.Root>
        </>
    );
}

// Note: the legacy IoCRow component was deleted alongside the static
// table — its render is inlined into VirtualIoCTable below to keep the
// virtualizer's per-row layout simple. If a non-virtualized fallback is
// ever needed, the JSX is reproduced verbatim there.

interface VirtualTableProps {
    iocs: IoCRecord[];
    total: number;
    canEdit: boolean;
    allMatching: boolean;
    selectedIds: Set<string>;
    setAllMatching: (v: boolean) => void;
    onToggleSelectRow: (id: string, indexInPage: number, shiftKey: boolean) => void;
    onTogglePageSelect: (allSelected: boolean) => void;
    onOpen: (id: string) => void;
}

/**
 * VirtualIoCTable wires TanStack Table v8 (column model + header rendering)
 * onto a viewport that's virtualized via @tanstack/react-virtual. The
 * existing IoCRow render is preserved unchanged — the virtualizer measures
 * each row and only the rows in (or near) the visible window are mounted.
 *
 * Quirks worth knowing:
 *   - The viewport must be a fixed-height scroll container; we use a 60vh
 *     min-height clamp so the page header remains visible.
 *   - We render the rows inside an absolutely-positioned wrapper that uses
 *     a translateY transform — this dodges the layout-thrash that
 *     position:relative on every row would cause.
 *   - The "select all matching" banner stays as a non-virtualized header
 *     row (it's at most one row, conditional on selection state).
 */
function VirtualIoCTable({
    iocs,
    total,
    canEdit,
    allMatching,
    selectedIds,
    setAllMatching,
    onToggleSelectRow,
    onTogglePageSelect,
    onOpen,
}: VirtualTableProps): React.ReactElement {
    // TanStack v8 column definitions. Used here for the header rendering
    // (so the column shape lives in one place); the body still uses the
    // legacy IoCRow component for visual parity with the rest of the SPA.
    const columnHelper = createColumnHelper<IoCRecord>();
    const columns = useMemo(() => {
        const cols = [];
        if (canEdit) {
            cols.push(
                columnHelper.display({
                    id: "select",
                    header: () => (
                        <HeaderCheckbox
                            total={iocs.length}
                            selectedOnPage={iocs.filter((i) => selectedIds.has(i.id)).length}
                            onToggle={onTogglePageSelect}
                        />
                    ),
                    size: 32,
                }),
            );
        }
        cols.push(
            columnHelper.accessor("type", { header: "Type", size: 80 }),
            columnHelper.accessor("value", { header: "Indicator" }),
            columnHelper.accessor("severity", { header: "Severity", size: 110 }),
            columnHelper.accessor("killchainPhase", { header: "Kill-chain", size: 140 }),
            columnHelper.accessor("sourceCount", { header: "Sources", size: 80 }),
            columnHelper.accessor("tags", { header: "Tags" }),
            columnHelper.accessor("lastSeen", { header: "Last seen", size: 160 }),
        );
        return cols;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [canEdit, iocs, selectedIds]);

    const table = useReactTable({
        data: iocs,
        columns,
        getCoreRowModel: getCoreRowModel(),
    });

    // 2026-04-26 (round 2): two issues from the previous revert —
    //   1. Column overlap. Row <td>s carried explicit widths (80/110/140/
    //      80/160) but the <th>s used `width:1` + tableLayout:auto. The
    //      browser then negotiated different widths between header and body
    //      cells and content (especially long URLs in the value column)
    //      bled into neighbours.
    //   2. Table appears short. The wrapper had maxHeight:70vh, so the
    //      table never flowed with the page — it scrolled internally and
    //      looked truncated below the fold.
    // Fix:
    //   * tableLayout:fixed + an explicit <colgroup> with widths that
    //     match the row <td>s. Now header and body share one widths source
    //     of truth.
    //   * Drop the wrapper's maxHeight/minHeight/overflow so the table
    //     uses the full page; the page scrolls, not the table.
    const showBulkBanner = canEdit && selectedIds.size === iocs.length && iocs.length > 0 && total > iocs.length;

    // Column widths — must agree with the explicit <td> widths below.
    // Two flexible columns (value, tags) split the residual space.
    const colWidths = {
        select: 36,
        type: 90,
        value: undefined as string | number | undefined, // flex
        severity: 130,
        killchain: 150,
        sourceCount: 80,
        tags: undefined as string | number | undefined, // flex
        lastSeen: 170,
    };

    return (
        <div>
            <table className="data-table" style={{ tableLayout: "fixed", width: "100%" }}>
                <colgroup>
                    {canEdit && <col style={{ width: colWidths.select }} />}
                    <col style={{ width: colWidths.type }} />
                    <col style={{ width: "auto" }} />{/* value */}
                    <col style={{ width: colWidths.severity }} />
                    <col style={{ width: colWidths.killchain }} />
                    <col style={{ width: colWidths.sourceCount }} />
                    <col style={{ width: "auto" }} />{/* tags */}
                    <col style={{ width: colWidths.lastSeen }} />
                </colgroup>
                <thead style={{ position: "sticky", top: 0, zIndex: 1, background: "var(--color-surface-raised)" }}>
                    {table.getHeaderGroups().map((hg) => (
                        <tr key={hg.id}>
                            {hg.headers.map((header) => (
                                <th
                                    key={header.id}
                                    style={{
                                        whiteSpace: "nowrap",
                                        textAlign: header.id === "sourceCount" ? "right" : undefined,
                                        overflow: "hidden",
                                        textOverflow: "ellipsis",
                                    }}
                                >
                                    {header.isPlaceholder
                                        ? null
                                        : flexRender(header.column.columnDef.header, header.getContext())}
                                </th>
                            ))}
                        </tr>
                    ))}
                </thead>
                <tbody>
                    {showBulkBanner && (
                        <tr>
                            <td
                                colSpan={canEdit ? 8 : 7}
                                style={{
                                    background: "var(--color-accent-soft)",
                                    textAlign: "center",
                                    padding: "var(--space-2)",
                                    fontSize: "var(--font-size-12)",
                                }}
                            >
                                All {iocs.length} on this page are selected.{" "}
                                {allMatching ? (
                                    <>
                                        All <strong>{total.toLocaleString()}</strong> matching filters are selected.{" "}
                                        <button
                                            type="button"
                                            className="btn btn--ghost btn--sm"
                                            onClick={() => setAllMatching(false)}
                                        >
                                            Select this page only
                                        </button>
                                    </>
                                ) : (
                                    <button
                                        type="button"
                                        className="btn btn--ghost btn--sm"
                                        onClick={() => setAllMatching(true)}
                                    >
                                        Select all <strong>{total.toLocaleString()}</strong> matching filters
                                    </button>
                                )}
                            </td>
                        </tr>
                    )}
                    {iocs.map((i, idx) => {
                        return (
                            <tr
                                key={i.id}
                                data-index={idx}
                                onClick={() => onOpen(i.id)}
                                tabIndex={0}
                                onKeyDown={(e) => {
                                    if (e.key === "Enter") onOpen(i.id);
                                }}
                                className={selectedIds.has(i.id) || allMatching ? "is-selected" : ""}
                                style={{
                                    cursor: "pointer",
                                    background:
                                        selectedIds.has(i.id) || allMatching
                                            ? "var(--color-accent-soft)"
                                            : undefined,
                                }}
                            >
                                {canEdit && (
                                    <td onClick={(e) => e.stopPropagation()}>
                                        <input
                                            type="checkbox"
                                            checked={selectedIds.has(i.id) || allMatching}
                                            onChange={() => {
                                                /* handled via click for shift support */
                                            }}
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                onToggleSelectRow(i.id, idx, e.shiftKey);
                                            }}
                                            aria-label={selectedIds.has(i.id) ? "Deselect row" : "Select row"}
                                            style={{ cursor: "pointer" }}
                                        />
                                    </td>
                                )}
                                <td style={{ whiteSpace: "nowrap" }}>
                                    <TypeBadge type={i.type} />
                                </td>
                                <td style={{ overflow: "hidden" }}>
                                    <span
                                        className="type-mono-sm"
                                        title={i.value}
                                        style={{
                                            display: "block",
                                            overflow: "hidden",
                                            textOverflow: "ellipsis",
                                            whiteSpace: "nowrap",
                                        }}
                                    >
                                        {i.value}
                                    </span>
                                </td>
                                <td style={{ whiteSpace: "nowrap" }}>
                                    <span
                                        className={`status-pill ${severityPillClass(i.severity)}`}
                                        style={{ borderColor: "transparent" }}
                                    >
                                        {i.severity} · {severityLabel(i.severity)}
                                    </span>
                                </td>
                                <td style={{ whiteSpace: "nowrap" }}>
                                    {i.killchainPhase ? (
                                        <span className="chip chip--info" style={{ height: 20 }}>
                                            {killchainLabel(i.killchainPhase)}
                                        </span>
                                    ) : (
                                        <span className="text-text-subtle text-12">—</span>
                                    )}
                                </td>
                                <td
                                    style={{ textAlign: "right" }}
                                    className="tabular text-13"
                                >
                                    {i.sourceCount}
                                </td>
                                <td style={{ overflow: "hidden" }}>
                                    <div className="tag-stack" style={{ flexWrap: "nowrap", overflow: "hidden" }}>
                                        {i.tags.slice(0, 4).map((t) => (
                                            <span key={t} className="chip" style={{ height: 20 }}>
                                                {t}
                                            </span>
                                        ))}
                                        {i.tags.length > 4 && (
                                            <span className="tag-stack__more">+{i.tags.length - 4}</span>
                                        )}
                                    </div>
                                </td>
                                <td
                                    className="text-12 text-text-muted"
                                    style={{ whiteSpace: "nowrap" }}
                                >
                                    {new Date(i.lastSeen).toLocaleString()}
                                </td>
                            </tr>
                        );
                    })}
                </tbody>
            </table>
        </div>
    );
}

function killchainLabel(phase: string): string {
    const entry = KILLCHAIN_PHASES.find((p) => p.slug === phase);
    return entry ? entry.label : phase;
}

function useDebounce<T>(value: T, delayMs: number, callback: (v: T) => void): void {
    useEffect(() => {
        const t = window.setTimeout(() => callback(value), delayMs);
        return () => window.clearTimeout(t);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [value, delayMs]);
}
